import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, TrendingUp, Calendar, LogOut, LayoutDashboard, Plus, FileText, UserCog, Settings, Bell, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0891b2', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#6366f1'];

function ManagerDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [backlogData, setBacklogData] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    const token = localStorage.getItem('token');
    
    // First load critical stats with timeout
    try {
      const statsRes = await axios.get(`${API}/dashboard/stats`, { 
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Dashboard stats error:', error);
      toast.error('Failed to load dashboard statistics');
      setLoading(false);
      return;
    }
    
    // Load additional data in background, don't block the UI
    try {
      const [backlogRes, sourceRes] = await Promise.all([
        axios.get(`${API}/reports/backlog-activities`, { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000 
        }),
        axios.get(`${API}/reports/source-effectiveness`, { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        })
      ]);
      setBacklogData(backlogRes.data.slice(0, 8));
      setSourceData(sourceRes.data.filter(s => s.total_leads > 0).slice(0, 6));
    } catch (error) {
      console.error('Dashboard additional data error:', error);
      // Don't show error for additional data, just log it
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="manager-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Manager Dashboard</h1>
            <p className="text-sm text-slate-600">{user?.full_name}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={() => toast.info('Notifications feature coming soon!')} data-testid="notifications-button">
              <Bell className="h-5 w-5" />
            </Button>
            <Button variant="outline" onClick={() => navigate('/settings')} data-testid="settings-button">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-cyan-700">{stats?.total_leads || 0}</div>
                  <div className="text-sm text-cyan-600 font-medium mt-1">Total Leads</div>
                </div>
                <Users className="h-10 w-10 text-cyan-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-green-700">{stats?.booked || 0}</div>
                  <div className="text-sm text-green-600 font-medium mt-1">Booked</div>
                </div>
                <TrendingUp className="h-10 w-10 text-green-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-amber-700">{stats?.new_leads || 0}</div>
                  <div className="text-sm text-amber-600 font-medium mt-1">New Leads</div>
                </div>
                <Calendar className="h-10 w-10 text-amber-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-purple-700">{stats?.conversion_rate || 0}%</div>
                  <div className="text-sm text-purple-600 font-medium mt-1">Conversion Rate</div>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Button
            className="h-14 bg-cyan-600 hover:bg-cyan-700 text-white font-medium"
            onClick={() => navigate('/leads/new')}
            data-testid="add-lead-button"
          >
            <Plus className="mr-2 h-5 w-5" />
            Add New Lead
          </Button>
          <Button
            className="h-14 bg-slate-700 hover:bg-slate-800 text-white font-medium"
            onClick={() => navigate('/leads')}
            data-testid="view-all-leads-button"
          >
            <LayoutDashboard className="mr-2 h-5 w-5" />
            View All Leads
          </Button>
          <Button
            className="h-14 bg-purple-600 hover:bg-purple-700 text-white font-medium"
            onClick={() => navigate('/reports')}
            data-testid="detailed-reports-button"
          >
            <FileText className="mr-2 h-5 w-5" />
            Detailed Reports
          </Button>
          {/* CHANGE 7: Calendar Button for Super Admin */}
          <Button
            className="h-14 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            onClick={() => navigate('/calendar')}
            data-testid="calendar-button"
          >
            <CalendarDays className="mr-2 h-5 w-5" />
            Calendar
          </Button>
          {/* Consolidated Management Button - Replaces separate Manage Team and Manage Projects */}
          <Button
            className="h-14 bg-amber-600 hover:bg-amber-700 text-white font-medium"
            onClick={() => navigate('/management')}
            data-testid="manage-projects-team-button"
          >
            <UserCog className="mr-2 h-5 w-5" />
            Manage Projects & Team
          </Button>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Team Backlog */}
          <Card data-testid="team-backlog-chart">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Team Backlog Activities</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={backlogData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="user" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="#0891b2" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Source Distribution */}
          <Card data-testid="source-distribution-chart">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Lead Source Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    dataKey="total_leads"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => `${entry.source}: ${entry.total_leads}`}
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value} leads`, name]}
                    contentStyle={{ fontSize: '12px' }}
                  />
                  {/* <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => {
                      const entry = sourceData.find(d => d.source === value);
                      return entry ? `${value} (${entry.total_leads})` : value;
                    }}
                    wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                  /> */}
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ManagerDashboard;
